import { ProductType } from 'common/api/e-comm/models/Product';

import type { EGiftReloadability, ReloadCandidate } from './types';

const EGIFT_FILTER_TYPES: ReadonlySet<ProductType> = Object.freeze(
    new Set([ProductType.EGiftCard])
);
const RELOADABLE_EGIFT_FILTER_TYPES: ReadonlySet<ProductType> = Object.freeze(
    new Set([ProductType.EGiftCard, ProductType.DigitalReload])
);

// Shared "no reloadable product" answer; frozen so callers cannot leak state into it.
export const NOT_RELOADABLE_EGIFT: EGiftReloadability<never> = Object.freeze({
    isReloadable: false,
    canAddMoney: false,
    reloadProduct: null,
    filterTypes: EGIFT_FILTER_TYPES,
});

// First merchandised (isVisible) DigitalReload product for the brand; hidden ones are never chosen.
export const pickDigitalReloadProduct = <P extends ReloadCandidate>(
    products: readonly P[],
    brandId: number
): P | null =>
    products.find(
        (product) =>
            product.type === ProductType.DigitalReload &&
            product.brandId === brandId &&
            product.isVisible === true
    ) ?? null;

// Card and product `isReloadable` flags are intentionally not inputs of this rule.
export const getEGiftReloadability = <P extends ReloadCandidate>(
    digitalReloadProduct: P | null | undefined
): EGiftReloadability<P> => {
    // Callers pass whatever they hold, so the type and visibility checks stay here.
    if (
        digitalReloadProduct == null ||
        digitalReloadProduct.type !== ProductType.DigitalReload ||
        digitalReloadProduct.isVisible !== true
    ) {
        return NOT_RELOADABLE_EGIFT;
    }
    const canAddMoney = digitalReloadProduct.isAvailable === true;
    return {
        isReloadable: true,
        canAddMoney,
        reloadProduct: canAddMoney ? digitalReloadProduct : null,
        filterTypes: RELOADABLE_EGIFT_FILTER_TYPES,
    };
};

// Convenience for callers that hold the brand's whole product list instead of the reload product.
export const getEGiftReloadabilityFromProducts = <P extends ReloadCandidate>(
    products: readonly P[],
    brandId: number
): EGiftReloadability<P> => getEGiftReloadability(pickDigitalReloadProduct(products, brandId));
